import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowDown, ArrowUp, Clock, Plus, Trash2, Users } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Card } from '@/components/ui/Card';
import { useAuthStore } from '@/stores/authStore';
import { listChannelLines, type ChannelLine } from '@/api/channels';
import { listDirectoryUsers, type DirectoryUser } from '@/api/directory';
import {
  addQueueMember,
  createEscalationTier,
  createRoutingQueue,
  createRoutingRule,
  deleteEscalationTier,
  deleteRoutingQueue,
  deleteRoutingRule,
  getEscalationLadder,
  getLineRoutingConfig,
  getRoutingCatalog,
  listQueueMembers,
  listRoutingQueues,
  listRoutingRules,
  putLineRoutingConfig,
  removeQueueMember,
  reorderRoutingRules,
  routingErrorDetail,
  updateQueueMember,
  updateRoutingQueue,
  type BusinessHoursWindow,
  type Destination,
  type EscalationLadder,
  type LineRoutingConfig,
  type PredicateKindSpec,
  type QueueMember,
  type RoutingCatalog,
  type RoutingQueue,
  type RoutingRule,
  type RulePredicate,
} from '@/api/conversationRouting';

/**
 * Ticket 138.6 — admin Web UI for conversation routing and escalation
 * (backend: `internal/routingadmin`, decisions D18/D19).
 *
 * Catalogue-driven, in the 137.7 sense: every enumerable value this page offers
 * — routing modes, predicate kinds, the operators each kind admits, the values
 * each kind accepts, weekday codes, escalation triggers — comes from
 * `GET /routing/catalog`. There is no TypeScript literal restating a Go enum
 * anywhere below, because a restated enum drifts silently: it still compiles
 * after the backend grows a value, and the only symptom is an admin who cannot
 * express a rule the engine would happily evaluate.
 *
 * Reads are open to any staff role; the write controls mount only for
 * TENANT_ADMIN / PLATFORM_ADMIN, mirroring the `auth.RequireRole` gate the
 * backend applies to the same verbs. The UI gate is convenience — the backend
 * gate is the one that decides.
 */

const WRITE_ROLES = ['TENANT_ADMIN', 'PLATFORM_ADMIN'];

export function ConversationRoutingPage() {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  const canWrite = WRITE_ROLES.includes(role ?? '');

  const [catalog, setCatalog] = useState<RoutingCatalog | null>(null);
  const [queues, setQueues] = useState<RoutingQueue[]>([]);
  const [lines, setLines] = useState<ChannelLine[]>([]);
  const [users, setUsers] = useState<DirectoryUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [tab, setTab] = useState<'queues' | 'lines'>('queues');

  // `t` is NOT referentially stable: useTranslation hands back a new function on
  // every render. Listing it in load's dependency array therefore rebuilt load on
  // every render, which re-ran the effect below, which called setLoading(true),
  // which rendered again — an unbounded refetch loop. Measured before this ref
  // existed: 452 calls to GET /routing/catalog in 300ms from a single mount.
  //
  // The visible symptom was not the network traffic but the LOST UI STATE. Each
  // loading flip unmounts QueuesTab, and `expanded` lives inside it, so an
  // expanded queue card collapsed underneath whatever the user (or a test) was
  // doing. That is what made the business-hours test flaky: it only failed when
  // the machine was loaded enough for one of those flips to land between the
  // click that expands the card and the assertion that reads it.
  //
  // A ref keeps the message translated with the CURRENT language while keeping
  // load's identity stable. This is the same hazard QueueCard.refreshDetail and
  // LinesTab.refresh already guard against, for the same reason.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cat, qs, ls, us] = await Promise.all([
        getRoutingCatalog(),
        listRoutingQueues(),
        listChannelLines(),
        listDirectoryUsers(),
      ]);
      setCatalog(cat);
      setQueues(qs);
      setLines(ls);
      setUsers(us);
    } catch (err) {
      setError(routingErrorDetail(err) ?? tRef.current('conversationRouting.error_load'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function report(err: unknown) {
    setError(routingErrorDetail(err) ?? t('conversationRouting.error_generic'));
    setNotice(null);
  }

  function announce(message: string) {
    setNotice(message);
    setError(null);
  }

  return (
    <div className="space-y-6" data-testid="conversation-routing-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Users className="h-6 w-6" />
          {t('conversationRouting.title')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('conversationRouting.subtitle')}</p>
      </div>

      {error && (
        <p data-testid="conversation-routing-error" className="text-sm text-red-400">
          {error}
        </p>
      )}
      {notice && (
        <p data-testid="conversation-routing-notice" className="text-sm text-emerald-300">
          {notice}
        </p>
      )}

      <div className="flex gap-2">
        <Button
          variant={tab === 'queues' ? 'default' : 'secondary'}
          size="sm"
          data-testid="conversation-routing-tab-queues"
          onClick={() => setTab('queues')}
        >
          {t('conversationRouting.tab_queues')}
        </Button>
        <Button
          variant={tab === 'lines' ? 'default' : 'secondary'}
          size="sm"
          data-testid="conversation-routing-tab-lines"
          onClick={() => setTab('lines')}
        >
          {t('conversationRouting.tab_lines')}
        </Button>
      </div>

      {loading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}

      {!loading && catalog && tab === 'queues' && (
        <QueuesTab
          catalog={catalog}
          queues={queues}
          users={users}
          canWrite={canWrite}
          onChanged={load}
          onError={report}
          onNotice={announce}
        />
      )}

      {!loading && catalog && tab === 'lines' && (
        <LinesTab
          catalog={catalog}
          queues={queues}
          lines={lines}
          users={users}
          canWrite={canWrite}
          onError={report}
          onNotice={announce}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ //
// Business hours editor
// ------------------------------------------------------------------ //

/**
 * The schedule editor.
 *
 * The empty state is a message, not a blank box: an empty window list means
 * ALWAYS OPEN, and an admin who reads a blank editor as "closed" would add a
 * 24/7 window to be safe — a different document with different behaviour at a
 * DST boundary. Weekday codes come from the catalogue, never from a literal
 * here, because they are the codes the Go parser accepts.
 */
function BusinessHoursEditor({
  weekdays,
  windows,
  disabled,
  onChange,
  testId,
}: {
  weekdays: string[];
  windows: BusinessHoursWindow[];
  disabled: boolean;
  onChange: (next: BusinessHoursWindow[]) => void;
  testId: string;
}) {
  const { t } = useTranslation();

  function patch(index: number, next: Partial<BusinessHoursWindow>) {
    onChange(windows.map((w, i) => (i === index ? { ...w, ...next } : w)));
  }

  function toggleDay(index: number, day: string) {
    const current = windows[index].days;
    patch(index, {
      days: current.includes(day) ? current.filter((d) => d !== day) : [...current, day],
    });
  }

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs font-medium">
        <Clock className="h-3 w-3" />
        {t('conversationRouting.business_hours')}
      </div>

      {windows.length === 0 && (
        <p data-testid={`${testId}-always-open`} className="text-xs text-muted-foreground">
          {t('conversationRouting.business_hours_always_open')}
        </p>
      )}

      {windows.map((w, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2 rounded border p-2">
          <div className="flex flex-wrap gap-1">
            {weekdays.map((day) => (
              <label key={day} className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={w.days.includes(day)}
                  onChange={() => toggleDay(i, day)}
                  data-testid={`${testId}-window-${i}-day-${day}`}
                />
                {t(`conversationRouting.weekday.${day}`, { defaultValue: day })}
              </label>
            ))}
          </div>
          <Input
            className="w-24"
            aria-label={t('conversationRouting.window_start')}
            data-testid={`${testId}-window-${i}-start`}
            disabled={disabled}
            value={w.start}
            onChange={(e) => patch(i, { start: e.target.value })}
          />
          <Input
            className="w-24"
            aria-label={t('conversationRouting.window_end')}
            data-testid={`${testId}-window-${i}-end`}
            disabled={disabled}
            value={w.end}
            onChange={(e) => patch(i, { end: e.target.value })}
          />
          {!disabled && (
            <Button
              variant="ghost"
              size="sm"
              data-testid={`${testId}-window-${i}-remove`}
              onClick={() => onChange(windows.filter((_, j) => j !== i))}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      ))}

      {!disabled && (
        <Button
          variant="secondary"
          size="sm"
          data-testid={`${testId}-add-window`}
          onClick={() => onChange([...windows, { days: [], start: '09:00', end: '18:00' }])}
        >
          <Plus className="h-3 w-3" />
          {t('conversationRouting.add_window')}
        </Button>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ //
// Destination picker
// ------------------------------------------------------------------ //

/** A QUEUE or a USER — the two kinds the catalogue serves, never both. */
function DestinationPicker({
  catalog,
  queues,
  users,
  value,
  disabled,
  onChange,
  testId,
}: {
  catalog: RoutingCatalog;
  queues: RoutingQueue[];
  users: DirectoryUser[];
  value: Destination | null;
  disabled: boolean;
  onChange: (next: Destination) => void;
  testId: string;
}) {
  const { t } = useTranslation();
  const kind = value?.kind ?? catalog.destinationKinds[0] ?? 'QUEUE';
  const options = kind === 'USER' ? users : queues;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        className="w-40"
        aria-label={t('conversationRouting.destination_kind')}
        data-testid={`${testId}-kind`}
        disabled={disabled}
        value={kind}
        onChange={(e) => onChange({ kind: e.target.value, id: '' })}
      >
        {catalog.destinationKinds.map((k) => (
          <option key={k} value={k}>
            {t(`conversationRouting.destinationKind.${k}`, { defaultValue: k })}
          </option>
        ))}
      </Select>
      <Select
        className="w-64"
        aria-label={t('conversationRouting.destination_target')}
        data-testid={`${testId}-id`}
        disabled={disabled}
        value={value?.id ?? ''}
        onChange={(e) => onChange({ kind, id: e.target.value })}
      >
        <option value="">{t('conversationRouting.destination_placeholder')}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {'name' in o ? o.name : o.email}
          </option>
        ))}
      </Select>
    </div>
  );
}

// ------------------------------------------------------------------ //
// Queues tab
// ------------------------------------------------------------------ //

function QueuesTab({
  catalog,
  queues,
  users,
  canWrite,
  onChanged,
  onError,
  onNotice,
}: {
  catalog: RoutingCatalog;
  queues: RoutingQueue[];
  users: DirectoryUser[];
  canWrite: boolean;
  onChanged: () => Promise<void>;
  onError: (err: unknown) => void;
  onNotice: (message: string) => void;
}) {
  const { t } = useTranslation();
  const [newQueueName, setNewQueueName] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  async function handleCreate() {
    try {
      await createRoutingQueue({
        name: newQueueName,
        businessHours: [],
        timezone: 'UTC',
      });
      setNewQueueName('');
      onNotice(t('conversationRouting.queue_created'));
      await onChanged();
    } catch (err) {
      onError(err);
    }
  }

  return (
    <div className="space-y-4">
      {canWrite && (
        <Card className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <label className="text-xs font-medium" htmlFor="new-queue-name">
              {t('conversationRouting.queue_name')}
            </label>
            <Input
              id="new-queue-name"
              data-testid="new-queue-name"
              value={newQueueName}
              onChange={(e) => setNewQueueName(e.target.value)}
            />
          </div>
          <Button data-testid="create-queue" disabled={!newQueueName.trim()} onClick={handleCreate}>
            <Plus className="h-4 w-4" />
            {t('conversationRouting.create_queue')}
          </Button>
        </Card>
      )}

      {queues.length === 0 && (
        <p data-testid="queues-empty" className="text-sm text-muted-foreground">
          {t('conversationRouting.queues_empty')}
        </p>
      )}

      {queues.map((q) => (
        <QueueCard
          key={q.id}
          catalog={catalog}
          queue={q}
          queues={queues}
          users={users}
          canWrite={canWrite}
          expanded={expanded === q.id}
          onToggle={() => setExpanded(expanded === q.id ? null : q.id)}
          onChanged={onChanged}
          onError={onError}
          onNotice={onNotice}
        />
      ))}
    </div>
  );
}

function QueueCard({
  catalog,
  queue,
  queues,
  users,
  canWrite,
  expanded,
  onToggle,
  onChanged,
  onError,
  onNotice,
}: {
  catalog: RoutingCatalog;
  queue: RoutingQueue;
  queues: RoutingQueue[];
  users: DirectoryUser[];
  canWrite: boolean;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => Promise<void>;
  onError: (err: unknown) => void;
  onNotice: (message: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(queue.name);
  const [timezone, setTimezone] = useState(queue.timezone);
  const [hours, setHours] = useState<BusinessHoursWindow[]>(queue.businessHours);
  const [members, setMembers] = useState<QueueMember[]>([]);
  const [ladder, setLadder] = useState<EscalationLadder | null>(null);
  const [newMemberId, setNewMemberId] = useState('');

  const refreshDetail = useCallback(async () => {
    try {
      const [ms, l] = await Promise.all([
        listQueueMembers(queue.id),
        getEscalationLadder(queue.id),
      ]);
      setMembers(ms);
      setLadder(l);
    } catch (err) {
      onError(err);
    }
    // onError is a stable closure over setState; re-running on its identity
    // would refetch the queue detail on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue.id]);

  useEffect(() => {
    if (expanded) void refreshDetail();
  }, [expanded, refreshDetail]);

  async function saveQueue() {
    try {
      await updateRoutingQueue(queue.id, {
        name,
        isActive: queue.isActive,
        isDefault: queue.isDefault,
        businessHours: hours,
        timezone,
      });
      onNotice(t('conversationRouting.queue_saved'));
      await onChanged();
    } catch (err) {
      onError(err);
    }
  }

  async function removeQueue() {
    try {
      await deleteRoutingQueue(queue.id);
      onNotice(t('conversationRouting.queue_deleted'));
      await onChanged();
    } catch (err) {
      onError(err);
    }
  }

  return (
    <Card className="space-y-3" data-testid={`queue-${queue.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold">{queue.name}</h3>
          <p className="text-xs text-muted-foreground">
            {t('conversationRouting.member_summary', {
              active: queue.activeMemberCount,
              total: queue.memberCount,
            })}
          </p>
        </div>
        <Button variant="secondary" size="sm" data-testid={`queue-toggle-${queue.id}`} onClick={onToggle}>
          {expanded ? t('conversationRouting.collapse') : t('conversationRouting.configure')}
        </Button>
      </div>

      {expanded && (
        <div className="space-y-4 border-t pt-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor={`queue-name-${queue.id}`}>
                {t('conversationRouting.queue_name')}
              </label>
              <Input
                id={`queue-name-${queue.id}`}
                data-testid={`queue-name-${queue.id}`}
                disabled={!canWrite}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor={`queue-tz-${queue.id}`}>
                {t('conversationRouting.timezone')}
              </label>
              <Input
                id={`queue-tz-${queue.id}`}
                data-testid={`queue-tz-${queue.id}`}
                disabled={!canWrite}
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              />
            </div>
          </div>

          <BusinessHoursEditor
            weekdays={catalog.weekdays}
            windows={hours}
            disabled={!canWrite}
            onChange={setHours}
            testId={`queue-hours-${queue.id}`}
          />

          {canWrite && (
            <div className="flex gap-2">
              <Button size="sm" data-testid={`queue-save-${queue.id}`} onClick={saveQueue}>
                {t('conversationRouting.save')}
              </Button>
              <Button
                size="sm"
                variant="destructive"
                data-testid={`queue-delete-${queue.id}`}
                onClick={removeQueue}
              >
                {t('conversationRouting.delete_queue')}
              </Button>
            </div>
          )}

          {/* -------------------------------------------------- members -- */}
          <div className="space-y-2 border-t pt-3">
            <h4 className="text-sm font-semibold">{t('conversationRouting.members')}</h4>
            {members.length === 0 && (
              <p data-testid={`queue-members-empty-${queue.id}`} className="text-xs text-muted-foreground">
                {t('conversationRouting.members_empty')}
              </p>
            )}
            {members.map((m) => (
              <div key={m.id} className="flex flex-wrap items-center gap-2 text-xs">
                <span className="w-56 truncate">{m.email}</span>
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    disabled={!canWrite}
                    checked={m.isActive}
                    data-testid={`member-active-${m.id}`}
                    onChange={async () => {
                      try {
                        await updateQueueMember(m.id, {
                          isActive: !m.isActive,
                          maxOpenConversations: m.maxOpenConversations,
                        });
                        await refreshDetail();
                        await onChanged();
                      } catch (err) {
                        onError(err);
                      }
                    }}
                  />
                  {t('conversationRouting.member_active')}
                </label>
                <Input
                  className="w-24"
                  type="number"
                  min={1}
                  aria-label={t('conversationRouting.capacity')}
                  data-testid={`member-capacity-${m.id}`}
                  disabled={!canWrite}
                  defaultValue={m.maxOpenConversations ?? ''}
                  onBlur={async (e) => {
                    const raw = e.target.value.trim();
                    try {
                      await updateQueueMember(m.id, {
                        isActive: m.isActive,
                        maxOpenConversations: raw === '' ? null : Number(raw),
                      });
                      await refreshDetail();
                    } catch (err) {
                      onError(err);
                    }
                  }}
                />
                <span className="text-muted-foreground">
                  {t('conversationRouting.assigned_count', { count: m.assignedCount })}
                </span>
                {canWrite && (
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`member-remove-${m.id}`}
                    onClick={async () => {
                      try {
                        await removeQueueMember(m.id);
                        await refreshDetail();
                        await onChanged();
                      } catch (err) {
                        onError(err);
                      }
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
            {canWrite && (
              <div className="flex flex-wrap items-center gap-2">
                <Select
                  className="w-64"
                  aria-label={t('conversationRouting.add_member')}
                  data-testid={`queue-add-member-select-${queue.id}`}
                  value={newMemberId}
                  onChange={(e) => setNewMemberId(e.target.value)}
                >
                  <option value="">{t('conversationRouting.member_placeholder')}</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.email}
                    </option>
                  ))}
                </Select>
                <Button
                  size="sm"
                  disabled={!newMemberId}
                  data-testid={`queue-add-member-${queue.id}`}
                  onClick={async () => {
                    try {
                      await addQueueMember(queue.id, { userId: newMemberId });
                      setNewMemberId('');
                      await refreshDetail();
                      await onChanged();
                    } catch (err) {
                      onError(err);
                    }
                  }}
                >
                  <Plus className="h-3 w-3" />
                  {t('conversationRouting.add_member')}
                </Button>
              </div>
            )}
          </div>

          {/* -------------------------------------------------- ladder --- */}
          <LadderEditor
            catalog={catalog}
            queue={queue}
            queues={queues}
            users={users}
            ladder={ladder}
            canWrite={canWrite}
            onChanged={async () => {
              await refreshDetail();
              await onChanged();
            }}
            onError={onError}
          />
        </div>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ //
// Escalation ladder editor
// ------------------------------------------------------------------ //

/**
 * The escalation ladder, with the finding the server used to keep to itself.
 *
 * `ladder.warning` is the backend's "the ladder ends nowhere" message: the
 * terminal rung escalates into a queue with no ACTIVE member, so a conversation
 * that climbs the whole ladder lands on nobody. Until this ticket that finding
 * existed only in a server log, which meant the admin who built the ladder was
 * the one person who never saw it. It renders here, above the rungs, and it is
 * a WARNING rather than a save-blocker on purpose: a ladder is built rung by
 * rung and the queue is staffed afterwards.
 */
function LadderEditor({
  catalog,
  queue,
  queues,
  users,
  ladder,
  canWrite,
  onChanged,
  onError,
}: {
  catalog: RoutingCatalog;
  queue: RoutingQueue;
  queues: RoutingQueue[];
  users: DirectoryUser[];
  ladder: EscalationLadder | null;
  canWrite: boolean;
  onChanged: () => Promise<void>;
  onError: (err: unknown) => void;
}) {
  const { t } = useTranslation();
  const [draftName, setDraftName] = useState('');
  const [draftDest, setDraftDest] = useState<Destination | null>(null);
  const [draftFirst, setDraftFirst] = useState('900');
  const [draftNext, setDraftNext] = useState('900');

  const nextPosition = useMemo(
    () => (ladder?.tiers.length ? Math.max(...ladder.tiers.map((x) => x.position)) + 1 : 0),
    [ladder],
  );

  return (
    <div className="space-y-2 border-t pt-3" data-testid={`ladder-${queue.id}`}>
      <h4 className="text-sm font-semibold">{t('conversationRouting.ladder')}</h4>

      {ladder?.warning && (
        <p
          data-testid={`ladder-warning-${queue.id}`}
          role="alert"
          className="flex items-start gap-2 rounded border border-amber-500/50 bg-amber-500/10 p-2 text-xs text-amber-200"
        >
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>
            {t('conversationRouting.ladder_ends_nowhere')}
            <span className="block opacity-70">{ladder.warning}</span>
          </span>
        </p>
      )}

      {ladder && ladder.tiers.length === 0 && (
        <p data-testid={`ladder-empty-${queue.id}`} className="text-xs text-muted-foreground">
          {t('conversationRouting.ladder_empty')}
        </p>
      )}

      {ladder?.tiers.map((tier) => (
        <div key={tier.id} className="flex flex-wrap items-center gap-2 text-xs">
          <span className="w-8 text-muted-foreground">#{tier.position}</span>
          <span className="w-48 truncate font-medium">{tier.name}</span>
          <span className="w-64 truncate text-muted-foreground">
            {describeDestination(tier.destination, queues, users, t)}
          </span>
          <span className="text-muted-foreground">
            {t('conversationRouting.sla_summary', {
              first: tier.firstResponseSeconds,
              next: tier.nextResponseSeconds,
            })}
          </span>
          {canWrite && (
            <Button
              variant="ghost"
              size="sm"
              data-testid={`tier-delete-${tier.id}`}
              onClick={async () => {
                try {
                  await deleteEscalationTier(tier.id);
                  await onChanged();
                } catch (err) {
                  onError(err);
                }
              }}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      ))}

      {canWrite && (
        <div className="flex flex-wrap items-end gap-2 rounded border p-2">
          <Input
            className="w-40"
            aria-label={t('conversationRouting.tier_name')}
            data-testid={`tier-new-name-${queue.id}`}
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
          />
          <DestinationPicker
            catalog={catalog}
            queues={queues}
            users={users}
            value={draftDest}
            disabled={false}
            onChange={setDraftDest}
            testId={`tier-new-dest-${queue.id}`}
          />
          <Input
            className="w-24"
            type="number"
            aria-label={t('conversationRouting.first_response_seconds')}
            data-testid={`tier-new-first-${queue.id}`}
            value={draftFirst}
            onChange={(e) => setDraftFirst(e.target.value)}
          />
          <Input
            className="w-24"
            type="number"
            aria-label={t('conversationRouting.next_response_seconds')}
            data-testid={`tier-new-next-${queue.id}`}
            value={draftNext}
            onChange={(e) => setDraftNext(e.target.value)}
          />
          <Button
            size="sm"
            disabled={!draftName.trim() || !draftDest?.id}
            data-testid={`tier-create-${queue.id}`}
            onClick={async () => {
              if (!draftDest) return;
              try {
                await createEscalationTier(queue.id, {
                  position: nextPosition,
                  name: draftName,
                  destination: draftDest,
                  firstResponseSeconds: Number(draftFirst),
                  nextResponseSeconds: Number(draftNext),
                });
                setDraftName('');
                setDraftDest(null);
                await onChanged();
              } catch (err) {
                onError(err);
              }
            }}
          >
            <Plus className="h-3 w-3" />
            {t('conversationRouting.add_tier')}
          </Button>
        </div>
      )}
    </div>
  );
}

function describeDestination(
  dest: Destination | null,
  queues: RoutingQueue[],
  users: DirectoryUser[],
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (!dest) return '—';
  if (dest.kind === 'USER') {
    return users.find((u) => u.id === dest.id)?.email ?? dest.id;
  }
  const q = queues.find((x) => x.id === dest.id);
  if (!q) return dest.id;
  return q.activeMemberCount === 0
    ? t('conversationRouting.queue_unstaffed', { name: q.name })
    : q.name;
}

// ------------------------------------------------------------------ //
// Lines tab: routing mode + rule builder
// ------------------------------------------------------------------ //

function LinesTab({
  catalog,
  queues,
  lines,
  users,
  canWrite,
  onError,
  onNotice,
}: {
  catalog: RoutingCatalog;
  queues: RoutingQueue[];
  lines: ChannelLine[];
  users: DirectoryUser[];
  canWrite: boolean;
  onError: (err: unknown) => void;
  onNotice: (message: string) => void;
}) {
  const { t } = useTranslation();
  const [lineId, setLineId] = useState(lines[0]?.id ?? '');
  const [config, setConfig] = useState<LineRoutingConfig | null>(null);
  const [rules, setRules] = useState<RoutingRule[]>([]);

  const refresh = useCallback(async () => {
    if (!lineId) return;
    try {
      const [cfg, rs] = await Promise.all([getLineRoutingConfig(lineId), listRoutingRules(lineId)]);
      setConfig(cfg);
      setRules(rs);
    } catch (err) {
      onError(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (lines.length === 0) {
    return (
      <p data-testid="lines-empty" className="text-sm text-muted-foreground">
        {t('conversationRouting.lines_empty')}
      </p>
    );
  }

  async function saveConfig(next: Partial<LineRoutingConfig>) {
    if (!config) return;
    const merged = { ...config, ...next };
    try {
      const saved = await putLineRoutingConfig(lineId, {
        mode: merged.mode,
        staticDestination: merged.staticDestination,
        fallbackQueueId: merged.fallbackQueueId,
        businessHours: merged.businessHours,
        timezone: merged.timezone,
        isActive: merged.isActive,
      });
      setConfig(saved);
      onNotice(t('conversationRouting.config_saved'));
    } catch (err) {
      onError(err);
    }
  }

  return (
    <div className="space-y-4">
      <Card className="space-y-3">
        <div className="space-y-1">
          <label className="text-xs font-medium" htmlFor="routing-line">
            {t('conversationRouting.line')}
          </label>
          <Select
            id="routing-line"
            data-testid="routing-line-select"
            value={lineId}
            onChange={(e) => setLineId(e.target.value)}
          >
            {lines.map((l) => (
              <option key={l.id} value={l.id}>
                {l.displayName || l.phoneNumber || l.providerLineId}
              </option>
            ))}
          </Select>
        </div>

        {config && (
          <>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="routing-mode">
                {t('conversationRouting.mode')}
              </label>
              <Select
                id="routing-mode"
                data-testid="routing-mode-select"
                disabled={!canWrite}
                value={config.mode}
                onChange={(e) => setConfig({ ...config, mode: e.target.value })}
              >
                {catalog.modes.map((m) => (
                  <option key={m} value={m}>
                    {t(`conversationRouting.modeLabel.${m}`, { defaultValue: m })}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-muted-foreground" data-testid="routing-mode-hint">
                {t(`conversationRouting.modeHint.${config.mode}`, { defaultValue: '' })}
              </p>
            </div>

            {config.mode === 'STATIC' && (
              <div className="space-y-1">
                <span className="text-xs font-medium">
                  {t('conversationRouting.static_destination')}
                </span>
                <DestinationPicker
                  catalog={catalog}
                  queues={queues}
                  users={users}
                  value={config.staticDestination}
                  disabled={!canWrite}
                  onChange={(d) => setConfig({ ...config, staticDestination: d })}
                  testId="static-destination"
                />
              </div>
            )}

            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="routing-fallback">
                {t('conversationRouting.fallback_queue')}
              </label>
              <Select
                id="routing-fallback"
                data-testid="routing-fallback-select"
                disabled={!canWrite}
                value={config.fallbackQueueId ?? ''}
                onChange={(e) =>
                  setConfig({ ...config, fallbackQueueId: e.target.value || null })
                }
              >
                <option value="">{t('conversationRouting.no_fallback')}</option>
                {queues.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.name}
                  </option>
                ))}
              </Select>
            </div>

            <BusinessHoursEditor
              weekdays={catalog.weekdays}
              windows={config.businessHours}
              disabled={!canWrite}
              onChange={(w) => setConfig({ ...config, businessHours: w })}
              testId="line-hours"
            />

            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="routing-line-tz">
                {t('conversationRouting.timezone')}
              </label>
              <Input
                id="routing-line-tz"
                data-testid="routing-line-tz"
                disabled={!canWrite}
                value={config.timezone}
                onChange={(e) => setConfig({ ...config, timezone: e.target.value })}
              />
            </div>

            {canWrite && (
              <Button size="sm" data-testid="routing-config-save" onClick={() => saveConfig({})}>
                {t('conversationRouting.save')}
              </Button>
            )}
          </>
        )}
      </Card>

      {config && config.mode !== 'STATIC' && (
        <RuleBuilder
          catalog={catalog}
          queues={queues}
          users={users}
          lineId={lineId}
          mode={config.mode}
          rules={rules}
          canWrite={canWrite}
          onChanged={refresh}
          onError={onError}
        />
      )}
    </div>
  );
}

/**
 * The rule builder.
 *
 * Rules are evaluated FIRST MATCH WINS in the order shown, so the order is the
 * semantics and reordering is a first-class verb rather than an edit to a
 * priority number. The reorder request names every rule of the line exactly
 * once — the backend refuses a partial list, because a half-applied reorder
 * leaves the unnamed rules interleaved in an order nobody chose.
 *
 * Every predicate widget is derived from `catalog.predicateKinds`: which
 * operators the kind admits, whether it needs a key, and the exact value set
 * when the kind is an enum. A kind the backend marks `requiresClassifier` is
 * offered only in AGENT_ASSISTED mode, because in any other mode it would
 * produce a rule that silently never matches.
 */
function RuleBuilder({
  catalog,
  queues,
  users,
  lineId,
  mode,
  rules,
  canWrite,
  onChanged,
  onError,
}: {
  catalog: RoutingCatalog;
  queues: RoutingQueue[];
  users: DirectoryUser[];
  lineId: string;
  mode: string;
  rules: RoutingRule[];
  canWrite: boolean;
  onChanged: () => Promise<void>;
  onError: (err: unknown) => void;
}) {
  const { t } = useTranslation();
  const usableKinds = useMemo(
    () => catalog.predicateKinds.filter((k) => !k.requiresClassifier || mode === 'AGENT_ASSISTED'),
    [catalog.predicateKinds, mode],
  );

  const [draftName, setDraftName] = useState('');
  const [draftDest, setDraftDest] = useState<Destination | null>(null);
  const [draftPredicates, setDraftPredicates] = useState<RulePredicate[]>([]);

  async function move(index: number, delta: number) {
    const next = [...rules];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    try {
      await reorderRoutingRules(
        lineId,
        next.map((r) => r.id),
      );
      await onChanged();
    } catch (err) {
      onError(err);
    }
  }

  return (
    <Card className="space-y-3" data-testid="rule-builder">
      <h3 className="font-semibold">{t('conversationRouting.rules')}</h3>
      <p className="text-xs text-muted-foreground">{t('conversationRouting.rules_order_hint')}</p>

      {rules.length === 0 && (
        <p data-testid="rules-empty" className="text-xs text-muted-foreground">
          {t('conversationRouting.rules_empty')}
        </p>
      )}

      {rules.map((rule, i) => (
        <div key={rule.id} className="space-y-1 rounded border p-2" data-testid={`rule-${rule.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-8 text-xs text-muted-foreground">{i + 1}</span>
            <span className="w-48 truncate text-sm font-medium">{rule.name}</span>
            <span className="w-64 truncate text-xs text-muted-foreground">
              {describeDestination(rule.destination, queues, users, t)}
            </span>
            {canWrite && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`rule-up-${rule.id}`}
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                >
                  <ArrowUp className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`rule-down-${rule.id}`}
                  disabled={i === rules.length - 1}
                  onClick={() => move(i, 1)}
                >
                  <ArrowDown className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`rule-delete-${rule.id}`}
                  onClick={async () => {
                    try {
                      await deleteRoutingRule(rule.id);
                      await onChanged();
                    } catch (err) {
                      onError(err);
                    }
                  }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </>
            )}
          </div>
          <ul className="ml-8 list-disc text-xs text-muted-foreground">
            {rule.predicates.map((p, j) => (
              <li key={j}>
                {t(`conversationRouting.predicateKind.${p.kind}`, { defaultValue: p.kind })}{' '}
                {p.key && <code>{p.key}</code>}{' '}
                {t(`conversationRouting.operator.${p.operator}`, { defaultValue: p.operator })}{' '}
                <strong>{p.value}</strong>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {canWrite && (
        <div className="space-y-2 rounded border p-2" data-testid="rule-draft">
          <div className="flex flex-wrap items-end gap-2">
            <Input
              className="w-48"
              aria-label={t('conversationRouting.rule_name')}
              data-testid="rule-draft-name"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
            />
            <DestinationPicker
              catalog={catalog}
              queues={queues}
              users={users}
              value={draftDest}
              disabled={false}
              onChange={setDraftDest}
              testId="rule-draft-dest"
            />
          </div>

          {draftPredicates.map((p, i) => (
            <PredicateRow
              key={i}
              kinds={usableKinds}
              operators={catalog.operators}
              predicate={p}
              index={i}
              onChange={(next) =>
                setDraftPredicates(draftPredicates.map((q, j) => (j === i ? next : q)))
              }
              onRemove={() => setDraftPredicates(draftPredicates.filter((_, j) => j !== i))}
            />
          ))}

          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              data-testid="rule-add-predicate"
              onClick={() =>
                setDraftPredicates([
                  ...draftPredicates,
                  {
                    kind: usableKinds[0]?.kind ?? '',
                    key: '',
                    operator: usableKinds[0]?.operators[0] ?? '',
                    value: usableKinds[0]?.valueVocabulary?.[0] ?? '',
                  },
                ])
              }
            >
              <Plus className="h-3 w-3" />
              {t('conversationRouting.add_predicate')}
            </Button>
            <Button
              size="sm"
              disabled={!draftName.trim() || !draftDest?.id || draftPredicates.length === 0}
              data-testid="rule-create"
              onClick={async () => {
                if (!draftDest) return;
                try {
                  await createRoutingRule(lineId, {
                    name: draftName,
                    destination: draftDest,
                    predicates: draftPredicates,
                  });
                  setDraftName('');
                  setDraftDest(null);
                  setDraftPredicates([]);
                  await onChanged();
                } catch (err) {
                  onError(err);
                }
              }}
            >
              {t('conversationRouting.create_rule')}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

/** One predicate row, entirely derived from the kind's catalogue entry. */
function PredicateRow({
  kinds,
  operators,
  predicate,
  index,
  onChange,
  onRemove,
}: {
  kinds: PredicateKindSpec[];
  operators: string[];
  predicate: RulePredicate;
  index: number;
  onChange: (next: RulePredicate) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const spec = kinds.find((k) => k.kind === predicate.kind);
  // The operator list is the KIND's, not the catalogue-wide union: offering
  // CONTAINS for an enum kind would build a rule the backend refuses at save.
  const allowedOperators = spec?.operators ?? operators;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`predicate-${index}`}>
      <Select
        className="w-48"
        aria-label={t('conversationRouting.predicate_kind')}
        data-testid={`predicate-${index}-kind`}
        value={predicate.kind}
        onChange={(e) => {
          const next = kinds.find((k) => k.kind === e.target.value);
          onChange({
            kind: e.target.value,
            key: '',
            operator: next?.operators[0] ?? '',
            value: next?.valueVocabulary?.[0] ?? '',
          });
        }}
      >
        {kinds.map((k) => (
          <option key={k.kind} value={k.kind}>
            {t(`conversationRouting.predicateKind.${k.kind}`, { defaultValue: k.kind })}
          </option>
        ))}
      </Select>

      {spec?.requiresKey && (
        <>
          {spec.keyVocabulary && spec.keyVocabulary.length > 0 ? (
            <Select
              className="w-40"
              aria-label={t('conversationRouting.predicate_key')}
              data-testid={`predicate-${index}-key`}
              value={predicate.key}
              onChange={(e) => onChange({ ...predicate, key: e.target.value })}
            >
              <option value="">{t('conversationRouting.predicate_key_placeholder')}</option>
              {spec.keyVocabulary.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </Select>
          ) : (
            <Input
              className="w-40"
              aria-label={t('conversationRouting.predicate_key')}
              data-testid={`predicate-${index}-key`}
              value={predicate.key}
              onChange={(e) => onChange({ ...predicate, key: e.target.value })}
            />
          )}
        </>
      )}

      <Select
        className="w-40"
        aria-label={t('conversationRouting.predicate_operator')}
        data-testid={`predicate-${index}-operator`}
        value={predicate.operator}
        onChange={(e) => onChange({ ...predicate, operator: e.target.value })}
      >
        {allowedOperators.map((op) => (
          <option key={op} value={op}>
            {t(`conversationRouting.operator.${op}`, { defaultValue: op })}
          </option>
        ))}
      </Select>

      {spec?.valueVocabulary && spec.valueVocabulary.length > 0 ? (
        <Select
          className="w-48"
          aria-label={t('conversationRouting.predicate_value')}
          data-testid={`predicate-${index}-value`}
          value={predicate.value}
          onChange={(e) => onChange({ ...predicate, value: e.target.value })}
        >
          {spec.valueVocabulary.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </Select>
      ) : (
        <Input
          className="w-48"
          aria-label={t('conversationRouting.predicate_value')}
          data-testid={`predicate-${index}-value`}
          value={predicate.value}
          onChange={(e) => onChange({ ...predicate, value: e.target.value })}
        />
      )}

      <Button variant="ghost" size="sm" data-testid={`predicate-${index}-remove`} onClick={onRemove}>
        <Trash2 className="h-3 w-3" />
      </Button>
    </div>
  );
}
